import React, { useCallback } from 'react';
import { FieldArrayRenderProps, useFieldArray } from 'react-final-form-arrays';

import { OfferFilter } from '@occc/graphql';
import { useChainId } from '@occc-app/Chain/hooks';

import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';
import { FulfillmentFormArrayItem } from './FulfillmentFormArrayItem';
import { fulfillmentItemInitialValues } from './constants';

import { FulfillmentFormValues } from '../../types';
import { AddAnotherItem } from '../AddAnotherItem';
import { FormArrayItemSection } from '../FormArrayItemSection';

type FulfillmentFormContentProps = {
  name: string;
};

export const FulfillmentFormContent: React.FC<FulfillmentFormContentProps> = React.memo(
  ({ name }) => {
    const { fields } = useFieldArray<FulfillmentFormValues>(name, {
      subscription: { length: true },
    });
    const handleAddItem = useCallback(() => fields.push(fulfillmentItemInitialValues), [fields]);
    const chainId = useChainId();
    const removeItem = useCallback((index: number) => fields.remove(index), [fields]);

    const { isEditable } = useCanEditFormFieldsContext();
    const isDeleteIconShown = isEditable && Boolean(fields.length && fields.length > 1);

    const makeOffersFilter = useCallback(
      (search: string): OfferFilter => {
        return {
          chainIdEquals: chainId,
          deleted: false,
          search: search || undefined,
        };
      },
      [chainId],
    );

    const renderContent: Parameters<
      FieldArrayRenderProps<FulfillmentFormValues, any>['fields']['map']
    >[0] = useCallback(
      (fieldArrayName, index) => {
        return (
          <React.Fragment key={fieldArrayName}>
            <FormArrayItemSection
              index={index}
              title='Member has'
              isDeleteIconShown={isDeleteIconShown}
              onDeleteClick={removeItem}
            />
            <FulfillmentFormArrayItem
              fieldArrayName={fieldArrayName}
              makeOffersFilter={makeOffersFilter}
            />
          </React.Fragment>
        );
      },
      [makeOffersFilter, removeItem, isDeleteIconShown],
    );

    return (
      <>
        {fields.map(renderContent)}
        <AddAnotherItem title='Add Another Fulfillment' onClick={handleAddItem} />
      </>
    );
  },
);
